import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { loadGoogleMaps } from "@/lib/googleMaps";
import { Loader2, MapPinOff, Route, LogIn, LogOut, Clock, Timer, CalendarDays } from "lucide-react";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const todayISO = () => new Date().toISOString().slice(0, 10);

function JourneyMap({ events }) {
  const mapRef = useRef(null);
  const gmap = useRef(null);
  const overlays = useRef([]);
  const [status, setStatus] = useState(KEY ? "loading" : "error");

  useEffect(() => {
    if (!KEY) return;
    let cancelled = false;
    loadGoogleMaps(KEY)
      .then((maps) => {
        if (cancelled || !mapRef.current) return;
        gmap.current = new maps.Map(mapRef.current, {
          center: { lat: 23.588, lng: 58.3829 },
          zoom: 12,
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
          clickableIcons: false,
          gestureHandling: "greedy",
          mapTypeControlOptions: { mapTypeIds: ["roadmap", "hybrid"] },
        });
        setStatus("ready");
      })
      .catch(() => !cancelled && setStatus("error"));
    return () => { cancelled = true; };
  }, []);

  // Redraw markers + route whenever events change and the map is ready.
  useEffect(() => {
    if (status !== "ready" || !gmap.current || !window.google) return;
    const maps = window.google.maps;
    overlays.current.forEach((o) => o.setMap(null));
    overlays.current = [];

    const pts = events.filter((e) => typeof e.latitude === "number" && typeof e.longitude === "number");
    if (pts.length === 0) return;

    const bounds = new maps.LatLngBounds();
    const path = [];
    const info = new maps.InfoWindow();

    pts.forEach((e) => {
      const pos = { lat: e.latitude, lng: e.longitude };
      path.push(pos);
      bounds.extend(pos);
      const isIn = e.type === "in";
      const color = isIn ? "#4F46E5" : "#0EA5E9";
      const marker = new maps.Marker({
        position: pos,
        map: gmap.current,
        label: { text: String(e.seq), color: "#ffffff", fontSize: "12px", fontWeight: "700" },
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 13,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2.5,
        },
        zIndex: e.seq,
      });
      marker.addListener("click", () => {
        info.setContent(
          `<div style="font-family:sans-serif;min-width:170px">
             <div style="font-weight:700;color:#0f172a;font-size:13px">${e.seq}. ${isIn ? "Clock In" : "Clock Out"}</div>
             <div style="color:#475569;font-size:12px;margin-top:2px">${e.site_name}</div>
             <div style="color:#6366f1;font-weight:600;font-size:12px;margin-top:2px">${e.time_local}</div>
           </div>`
        );
        info.open(gmap.current, marker);
      });
      overlays.current.push(marker);
    });

    if (path.length > 1) {
      const line = new maps.Polyline({
        path,
        map: gmap.current,
        strokeColor: "#6366F1",
        strokeOpacity: 0.9,
        strokeWeight: 3.5,
        icons: [{ icon: { path: maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3, fillColor: "#4F46E5", fillOpacity: 1, strokeColor: "#4F46E5" }, offset: "50%", repeat: "120px" }],
      });
      overlays.current.push(line);
    }

    if (pts.length === 1) {
      gmap.current.setCenter(path[0]);
      gmap.current.setZoom(16);
    } else {
      gmap.current.fitBounds(bounds, 60);
    }
  }, [events, status]);

  if (status === "error")
    return (
      <div className="h-full min-h-[420px] rounded-xl border border-amber-200 bg-amber-50 p-6 grid place-items-center text-center" data-testid="journey-map-error">
        <div className="space-y-2">
          <MapPinOff className="h-7 w-7 text-amber-500 mx-auto" />
          <p className="text-sm text-amber-800">Map couldn't load. Check the Google Maps key and that the Maps JavaScript API is enabled.</p>
        </div>
      </div>
    );

  return (
    <div className="relative h-full min-h-[420px] rounded-xl overflow-hidden border border-slate-200" data-testid="journey-map">
      <div ref={mapRef} className="absolute inset-0 bg-slate-100" />
      {status === "loading" && (
        <div className="absolute inset-0 grid place-items-center bg-slate-50 z-10">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
        </div>
      )}
    </div>
  );
}

export default function Journey() {
  const [employeeId, setEmployeeId] = useState("");
  const [dateVal, setDateVal] = useState(todayISO());

  const { data: filters } = useQuery({
    queryKey: ["report-filters"],
    queryFn: () => api.get("/admin/reports/filters"),
  });
  const employees = useMemo(() => filters?.employees || [], [filters]);

  useEffect(() => {
    if (!employeeId && employees.length) setEmployeeId(employees[0].id);
  }, [employees, employeeId]);

  const { data: journey, isFetching } = useQuery({
    queryKey: ["journey", employeeId, dateVal],
    queryFn: () => api.get(`/admin/reports/journey?employee_id=${employeeId}&date=${dateVal}`),
    enabled: !!employeeId && !!dateVal,
  });

  const events = useMemo(() => journey?.events || [], [journey]);
  const visits = useMemo(() => journey?.visits || [], [journey]);

  // Map an "out" event's timestamp to the visit it closes (to show duration inline).
  const durByOutTs = useMemo(() => {
    const m = {};
    visits.forEach((v) => { if (v.departure) m[v.departure] = v; });
    return m;
  }, [visits]);
  const ongoingVisit = visits.find((v) => v.ongoing);

  return (
    <div className="space-y-5 animate-fade-up" data-testid="journey-page">
      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Employee</label>
          <select
            data-testid="journey-employee-select"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            {employees.length === 0 && <option value="">No employees</option>}
            {employees.map((e) => (
              <option key={e.id} value={e.id}>{e.name} {e.staff_id ? `(${e.staff_id})` : ""}</option>
            ))}
          </select>
        </div>
        <div className="sm:w-56">
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Date</label>
          <div className="relative">
            <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <input
              type="date"
              data-testid="journey-date-input"
              value={dateVal}
              max={todayISO()}
              onChange={(e) => setDateVal(e.target.value)}
              className="w-full h-11 rounded-lg border border-slate-200 bg-white pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
        </div>
        {journey?.timezone && (
          <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-2.5 text-center" data-testid="journey-total">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">Total on-site · {journey.timezone.replace("_", " ")}</p>
            <p className="text-lg font-extrabold text-indigo-700 leading-tight">{journey.total_duration_label || "0m"}</p>
          </div>
        )}
      </div>

      {/* Map + timeline */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <JourneyMap events={events} />
        </div>

        {/* Timeline sidebar */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col" data-testid="journey-timeline">
          <div className="flex items-center gap-2 mb-4">
            <Route className="h-5 w-5 text-indigo-600" />
            <h3 className="font-head text-lg font-bold text-slate-900">Journey Timeline</h3>
          </div>

          {isFetching ? (
            <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
          ) : events.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm" data-testid="journey-empty">No attendance events for this day.</div>
          ) : (
            <ol className="relative space-y-4 pl-2">
              {events.map((e, idx) => {
                const isIn = e.type === "in";
                const closed = !isIn && durByOutTs[e.ts];
                return (
                  <li key={e.seq} className="relative pl-8" data-testid={`journey-event-${e.seq}`}>
                    {idx < events.length - 1 && <span className="absolute left-[11px] top-6 bottom-[-16px] w-px bg-slate-200" />}
                    <span className={`absolute left-0 top-0.5 grid place-items-center h-6 w-6 rounded-full text-white text-[11px] font-bold ${isIn ? "bg-indigo-600" : "bg-sky-500"}`}>
                      {e.seq}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {isIn ? <LogIn className="h-3.5 w-3.5 text-indigo-600" /> : <LogOut className="h-3.5 w-3.5 text-sky-500" />}
                      <span className="text-sm font-bold text-slate-800">{isIn ? "Clock In" : "Clock Out"}</span>
                      <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><Clock className="h-3 w-3" /> {e.time_local}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{e.site_name}</p>
                    {closed && (
                      <div className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                        <Timer className="h-3.5 w-3.5" /> {closed.duration_label} at {closed.site_name}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}

          {ongoingVisit && (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs font-semibold text-amber-700 flex items-center gap-1.5" data-testid="journey-ongoing">
              <Timer className="h-3.5 w-3.5" /> Still on site at {ongoingVisit.site_name} — {ongoingVisit.duration_label} so far
            </div>
          )}

          {visits.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Time Spent per Visit</p>
              <div className="space-y-2">
                {visits.map((v, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-sm" data-testid={`journey-visit-${i}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-700 truncate">{v.site_name}</p>
                      <p className="text-[11px] text-slate-400">{v.arrival_local} → {v.departure_local || "now"}</p>
                    </div>
                    <span className="flex-shrink-0 rounded-md bg-slate-100 text-slate-700 font-mono text-xs font-bold px-2 py-1">{v.duration_label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
